<template>
  <section class="driver-form">
    <header v-if="showHeaders === true">Водитель</header>

    <VFormItem
      label="ФИО водителя"
      layout="vertical"
      :with-asterisk="$core.utils.isNullOrUndefined(transportOrder) === true || transportOrder.isService === true"
    >
      <!-- создание зтк - подставляем водителя из избранного,
           по умолчанию поле пустое, поиск по ФИО и госномеру -->
      <VSuggestionSelectV2
        v-if="$core.utils.isNullOrUndefined(transportOrder) === false"
        v-model="transportOrder.driver"
        :type="Driver"
        :required="transportOrder.isService"
        :data="driverSource"
        placeholder="Введите ФИО водителя или госномер для поиска"
      />

      <!-- обычный ввод фио для создания в карточке -->
      <VInput
        v-else
        v-model="driver.name"
        required
        name
        :max-length="50"
      />

      <VButton
        theme="link"
        class="favorite"
        :disabled="$core.utils.isNullOrUndefined(currentDriver) === true"
        :title="DriversService.getFavoriteTitle(currentDriver)"
        :material="DriversService.getFavoriteIcon(currentDriver)"
        hover-material="star"
        @click="currentDriver.isFavorite = currentDriver.isFavorite === false"
      />
    </VFormItem>

    <VInput
      :model-value="currentDriver?.licenseNumber ?? null"
      @update:model-value="value => currentDriver.licenseNumber = value"
      label="Номер водительского удостоверения"
      layout="vertical"
      :disabled="$core.utils.isNullOrUndefined(currentDriver) === true"
      :disabled-reasons="transportOrder?.getReasonCannotEditDriver() ?? null"
    />

    <VInputPhoneNumber
      :model-value="currentDriver?.phone ?? null"
      @update:model-value="value => currentDriver.phone = value"
      label="Номер телефона"
      layout="vertical"
      :disabled="$core.utils.isNullOrUndefined(currentDriver) === true"
      :disabled-reasons="transportOrder?.getReasonCannotEditDriver() ?? null"
      phone
      :max-length="20"
    />

    <VInput
      :model-value="currentDriver?.inn ?? null"
      @update:model-value="value => currentDriver.inn = value"
      label="ИНН"
      layout="vertical"
      :disabled="$core.utils.isNullOrUndefined(currentDriver) === true"
      :disabled-reasons="transportOrder?.getReasonCannotEditDriver() ?? null"
    />
  </section>

  <hr v-if="showHeaders === true">

  <section class="driver-form">
    <header v-if="showHeaders === true">Транспорт</header>

    <VSelectV2
      class="vehicle"
      :model-value="vehicleHolder?.vehicle ?? null"
      @update:model-value="value => vehicleHolder.vehicle = value"
      label="Транспортное средство"
      layout="vertical"
      :required="transportOrder?.isService === true"
      :disabled="$core.utils.isNullOrUndefined(vehicleHolder) === true"
      :data="vehicleSource"
    />

    <VInput
      :model-value="vehicleHolder?.registrationNumber ?? null"
      @update:model-value="value => vehicleHolder.registrationNumber = value"
      label="Госномер"
      layout="vertical"
      :disabled="$core.utils.isNullOrUndefined(vehicleHolder) === true"
    />

    <VSelectV2
      :model-value="currentDriver?.vehicleOwnershipType ?? null"
      @update:model-value="value => currentDriver.vehicleOwnershipType = value"
      label="Тип владения транспортным средством"
      layout="vertical"
      :disabled="$core.utils.isNullOrUndefined(currentDriver) === true"
      :disabled-reasons="transportOrder?.getReasonCannotEditDriver() ?? null"
      :data="VehiclesService.vehicleOwnershipTypes"
    />
  </section>
</template>

<style lang="scss" src="./DriverForm.scss"/>

<script lang="ts">

import Driver          from '@/domains/Drivers/Driver';
import DriversService  from '@/domains/Drivers/DriversService';
import VehiclesService from '@/domains/Vehicles/VehiclesService';

import type TransportOrder from '@/domains/TransportOrders/TransportOrder';

/**
 * Поля водителя: ФИО, госномер, транспортное средство, форма владения, телефон, ИНН, водительское удостоверение и звезда избранного.
 * Две секции со своими заголовками — «Водитель» и «Транспорт», разделитель между ними ставит сама форма.
 * Правит водителя на месте; когда сохранять — решает родитель.
 *
 * Передаётся одно из двух:
 * - driver — карточка водителя: ФИО правится как поле водителя, транспорт и госномер — его обычные;
 * - transportOrder — ЗТК: ФИО — выбор существующего водителя либо неявное создание нового вводом,
 *   выбранный пишется в transportOrder.driver; транспорт и госномер — реквизиты самой ЗТК,
 *   доступны и без водителя
 */
export default defineComponent({
  name: 'DriverForm',

  props: {
    /** Водитель из карточки водителя */
    driver: {
      type:    Driver as PropType<Driver | null>,
      default: null,
    },

    /** ЗТК, водителя которой правим. Водителя может не быть, пока его не выбрали: поля водителя недоступны */
    transportOrder: {
      type:    Object as PropType<TransportOrder | null>,
      default: null,
    },

    /** Заголовки «Водитель» / «Транспорт» и разделитель между ними. false — поля встраиваются в чужой блок */
    showHeaders: {
      type:    Boolean,
      default: true,
    },
  },

  setup() {
    return {
      Driver,
      DriversService,
      VehiclesService,
    };
  },

  data() {
    /**
     * Водители — поиск по ФИО или госномеру, без зависимостей.
     *
     * принцип работы:
     * при создании зтк по умолчанию получаем водителя из избранного,
     * если зтк уже созданная, но без водителя, поле будет пустым,
     * не выводим список целиком иначе опции не будут обновляться
     *
     * поле пустое, поиск по ФИО или госномеру, если сотрем значение, то получим весь список,
     * это не задуманное поведение, а нюанс контролера, но он не мешает на данном этапе
     */
    const driverSource = this.$core.loader.select(({ pattern }) => DriversService.getList({ pattern }));

    /**
     * Транспортные средства — поиск по справочнику, без зависимостей.
     */
    const vehicleSource = this.$core.loader.select(({ pattern }) => VehiclesService.getList({ pattern }))
      .preloadOnFocus();

    return {
      driverSource,
      vehicleSource,
    };
  },

  computed: {
    /** Водитель, чьи поля правим: в ЗТК — выбранный в ней, в карточке — переданный */
    currentDriver(): Driver | null {
      if (this.$core.utils.isNullOrUndefined(this.transportOrder) === false) {
        return this.transportOrder.driver;
      }
      else {
        return this.driver;
      }
    },

    /** Чьи транспорт и госномер правим: в ЗТК — её собственные реквизиты, в карточке — обычные водителя */
    vehicleHolder(): TransportOrder | Driver | null {
      if (this.$core.utils.isNullOrUndefined(this.transportOrder) === false) {
        return this.transportOrder;
      }
      else {
        return this.driver;
      }
    },

  },
});
</script>
